import "react-phone-number-input/style.css";

import { useAppLocale } from "@/features/i18n/LocaleProvider";
import { formComponents as fc } from "@/features/i18n/formComponents";
import { ui } from "@patient-watch/ui";
import { invalidateProfileQuery, patchProfileQueryData } from "@/features/profile/api/selectProfile";
import { clearOnboardingContactSkip } from "@/features/onboarding/utils/onboardingContactSkip";
import { supabaseClient } from "@/lib/supabase";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "@patient-watch/ui/sonner";
import { useToastCopy } from "@/features/i18n/toastCopy";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import * as z from "zod";

const formSchema = z.object({
    old_phone: z.string().optional(),
    new_phone: z
        .string()
        .min(1, "Phone number required")
        .refine((value) => isPossiblePhoneNumber(value), "Not a valid phone number"),
});

type FormSchemaType = z.infer<typeof formSchema>;

interface Props {
    currentPhone: string | null;
    userId: string;
    onSuccess?: () => void;
}

const UpdatePhoneForm = ({ currentPhone, userId, onSuccess }: Props) => {
    const { locale } = useAppLocale();
    const toastCopy = useToastCopy();
    const queryClient = useQueryClient();

    const form = useForm<FormSchemaType>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            old_phone: currentPhone ? `+${currentPhone}` : "",
            new_phone: "",
        },
    });

    const updatePhoneMutation = useMutation({
        mutationFn: async ({ new_phone, user_id }: { new_phone: string; user_id: string }) => {
            const { data, error } = await supabaseClient.functions.invoke("public-update-user-phone", {
                body: {
                    new_phone: new_phone.replace("+", ""),
                    user_id,
                },
            });
            if (error) throw error;
            if (data?.error) throw new Error(data.error);
            return data;
        },
        onSuccess: (_data, variables) => {
            toast.success(toastCopy.phoneUpdatedSuccess);
            patchProfileQueryData(queryClient, userId, { phone: variables.new_phone.replace("+", "") });
            void clearOnboardingContactSkip(userId, "phone")
                .then((userMetaData) => {
                    patchProfileQueryData(queryClient, userId, { user_meta_data: userMetaData });
                })
                .catch(() => undefined);
            void invalidateProfileQuery(queryClient, userId);
            void queryClient.invalidateQueries({ queryKey: ["profiles"] });
            void queryClient.invalidateQueries({ queryKey: ["diary"] });
            onSuccess?.();
        },
        onError: (error) => {
            console.error("Error updating phone:", error);
            toast.error(toastCopy.failedUpdatePhone);
        },
    });

    const onSubmit = (data: FormSchemaType) => {
        const payload = {
            new_phone: data.new_phone,
            user_id: userId,
        };
        console.log("Submitting phone data:", payload);
        updatePhoneMutation.mutate(payload);
    };

    return (
        <ui.Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="form">
                {currentPhone && <fc.Phone name="old_phone" label="Current Phone" disabled />}

                <fc.Phone name="new_phone" label={currentPhone ? "New Phone" : "Phone"} autoFocus locale={locale} />

                <fc.SaveButton isLoading={updatePhoneMutation.isPending} className="w-full" />
            </form>
        </ui.Form>
    );
};

export default UpdatePhoneForm;
